import {FC, iterChildren} from '@gongxigames/framework/core/fragments/BuilderY';
import {createPropsGetter, mapSignalArray} from '../utils';
import {Store, Signal} from '@gongxigames/framework/core/fragments/store/Store';
import {JSX} from '../jsx-runtime';

export type PropsFor<T = any> = {
    /**
     * Data items to iterate on
     */
    items: T[] | Signal<T[]>;

    /**
     * Fallback component if `items` array is empty
     */
    fallback?: JSX.Element;

    /**
     * Factory map function to create components
     */
    children: (item: T, index: Signal<number>) => JSX.Element;
};

/**
 * Iterates over an array of data and calls a factory map function for each item.
 * When items are removed from the array destroys them.
 * When new items added to array calls the factory map function only for the new items.
 *
 * ```ts
 * const items = Store.signal([0,11,2,34]);
 * return <Container>
 *     <For items={items}>{ (n) => <Text text={n}/> }</For>
 * </Container>;
 * ```
 */
export const $For: FC<PropsFor> = (props) => {
    const items = Store.isSignal(props.items) ? props.items : Store.signal(props.items);
    const children = mapSignalArray(items, props.children, (data, node) => {
        for (const child of iterChildren(node)) {
            if(child.destroy && typeof child.destroy ==='function') {
                child.destroy({children: true});
            }
        }
    });

    return Store.computed(() => {
        const {fallback} = createPropsGetter(props);

        if (!children.value.length) {
            return fallback;
        }

        return children.value;
    });
};